import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addDays, endOfMonth, format, startOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, Pencil, Wrench, Clock, KeyRound, Printer } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AppuntamentoDialog } from "@/components/AppuntamentoDialog";
import { PromemoriaButton } from "@/components/PromemoriaButton";
import { messaggioConferma } from "@/lib/whatsapp";

const ymd = (d) => format(d, "yyyy-MM-dd");
const STATO_APP = { programmato: "border-l-orange-500", completato: "border-l-emerald-500 opacity-70", annullato: "border-l-slate-300 opacity-50 line-through" };
const fine = (ora, min) => { const [h, m] = ora.split(":").map(Number); const t = h * 60 + m + min; return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };

const AREA_BADGE = { officina: "bg-slate-100 text-slate-700", noleggio: "bg-sky-50 text-sky-700" };

const AppCard = ({ a, negozio, onEdit, onStato, onDelete, onScheda, onReload }) => (
  <li className={`rounded-md border border-l-4 border-slate-200 bg-white p-4 shadow-sm ${STATO_APP[a.stato]}`} data-testid={`app-card-${a.id}`}>
    <div className="flex flex-wrap items-start gap-3">
      <div className="w-24 shrink-0 font-mono text-sm font-bold text-slate-900"><Clock className="mr-1 inline h-3.5 w-3.5 text-orange-600" />{a.ora}<p className="text-[11px] font-normal text-slate-400">fino {fine(a.ora, a.durata_min)}</p></div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">{a.cliente_nome} {a.non_registrato && <span className="ml-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700" data-testid={`app-non-registrato-${a.id}`}>Non registrato</span>} <span className={`ml-1 rounded-sm px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${AREA_BADGE[a.area || "officina"]}`} data-testid={`app-area-badge-${a.id}`}>{a.area === "noleggio" ? "Noleggio" : "Officina"}</span> <span className="rounded-sm bg-orange-50 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-orange-700">{a.tipo}</span></p>
        <p className="text-sm text-slate-600">{a.bici_desc || "Nessuna bici indicata"}{a.cliente_telefono ? ` · ${a.cliente_telefono}` : ""}</p>
        {a.note && <p className="mt-1 text-sm text-slate-500">{a.note}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Select value={a.stato} onValueChange={(v) => onStato(a, v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`app-stato-select-${a.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="programmato">Programmato</SelectItem><SelectItem value="completato">Completato</SelectItem><SelectItem value="annullato">Annullato</SelectItem></SelectContent>
        </Select>
        <WhatsAppButton compact telefono={a.cliente_telefono} text={messaggioConferma(a, negozio)} testId={`app-whatsapp-${a.id}`} />
        {a.stato === "programmato" && <PromemoriaButton a={a} negozio={negozio} onDone={onReload} />}
        {!(a.non_registrato && a.area === "noleggio") && <Button variant="outline" size="icon" title={a.area === "noleggio" ? "Crea noleggio" : "Apri scheda di lavoro"} onClick={() => onScheda(a)} data-testid={`app-scheda-${a.id}`}>{a.area === "noleggio" ? <KeyRound className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}</Button>}
        <Button variant="ghost" size="icon" onClick={() => onEdit(a)} data-testid={`app-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
        <ConfirmDelete testId={`app-delete-${a.id}`} onConfirm={() => onDelete(a.id)} label="Elimina appuntamento" description="L'appuntamento verrà eliminato definitivamente." />
      </div>
    </div>
  </li>
);

export default function Agenda() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [giorno, setGiorno] = useState(new Date());
  const [mese, setMese] = useState(new Date());
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [bici, setBici] = useState([]);
  const [flotta, setFlotta] = useState([]);
  const [area, setArea] = useState("tutti");
  const [negozio, setNegozio] = useState("");
  const [dlg, setDlg] = useState(null);

  const load = useCallback(() => api.get("/appuntamenti", { params: { da: ymd(startOfMonth(mese)), a: ymd(endOfMonth(mese)) } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e))), [mese]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/clienti"), api.get("/bici"), api.get("/impostazioni"), api.get("/bici_noleggio")]).then(([c, b, i, f]) => {
      setClienti(c.data); setBici(b.data); setNegozio(i.data.nome_negozio || ""); setFlotta(f.data);
    });
    if (sp.get("cliente")) {
      setDlg({ cliente_id: sp.get("cliente"), bici_id: sp.get("bici") || "", data: ymd(new Date()), tipo: "Tagliando" });
      setSp({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const vai = (d) => { setGiorno(d); setMese(d); };
  const filtrati = items.filter((a) => area === "tutti" || (a.area || "officina") === area);
  const delGiorno = filtrati.filter((a) => a.data === ymd(giorno));
  const giorniOccupati = [...new Set(filtrati.filter((a) => a.stato !== "annullato").map((a) => a.data))].map((s) => new Date(`${s}T00:00`));

  const onSaved = (a) => { const d = new Date(`${a.data}T00:00`); vai(d); load(); };
  const onStato = async (a, stato) => {
    try { await api.put(`/appuntamenti/${a.id}`, { ...a, stato }); toast.success("Stato aggiornato"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const onDelete = async (id) => {
    try { await api.delete(`/appuntamenti/${id}`); toast.success("Appuntamento eliminato"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const stampa = (vista) => navigate(`/agenda/stampa?vista=${vista}&data=${ymd(giorno)}&area=${area}`);

  return (
    <div>
      <PageHeader overline="Officina e noleggio" title="Agenda appuntamenti">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="bg-white" data-testid="agenda-stampa-button"><Printer className="mr-1 h-4 w-4" /> Stampa</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => stampa("giorno")} data-testid="agenda-stampa-giorno">Stampa giorno ({format(giorno, "d MMM", { locale: it })})</DropdownMenuItem>
            <DropdownMenuItem onClick={() => stampa("settimana")} data-testid="agenda-stampa-settimana">Stampa settimana</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tabs value={area} onValueChange={setArea}>
          <TabsList className="bg-white ring-1 ring-slate-200">
            <TabsTrigger value="tutti" data-testid="agenda-filter-tutti">Tutti</TabsTrigger>
            <TabsTrigger value="officina" data-testid="agenda-filter-officina">Officina</TabsTrigger>
            <TabsTrigger value="noleggio" data-testid="agenda-filter-noleggio">Noleggio</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button onClick={() => setDlg({ data: ymd(giorno), ...(area === "noleggio" ? { area: "noleggio", tipo: "Ritiro noleggio" } : {}) })} className="bg-orange-600 hover:bg-orange-700" data-testid="agenda-new-button"><Plus className="mr-1 h-4 w-4" /> Nuovo appuntamento</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
        <div className="h-fit rounded-lg border border-slate-200 bg-white p-2 shadow-sm" data-testid="agenda-calendar">
          <Calendar mode="single" locale={it} selected={giorno} onSelect={(d) => d && setGiorno(d)} month={mese} onMonthChange={setMese}
            modifiers={{ occupato: giorniOccupati }} modifiersClassNames={{ occupato: "font-bold text-orange-700 underline decoration-orange-500 decoration-2 underline-offset-4" }} />
          <p className="px-3 pb-2 text-[11px] text-slate-400">I giorni sottolineati hanno appuntamenti.</p>
        </div>
        <section>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => vai(addDays(giorno, -1))} data-testid="agenda-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" onClick={() => vai(addDays(giorno, 1))} data-testid="agenda-next-day"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={() => vai(new Date())} data-testid="agenda-today">Oggi</Button>
            <h2 className="ml-2 font-heading text-lg font-bold capitalize text-slate-900" data-testid="agenda-giorno-title">{format(giorno, "EEEE d MMMM yyyy", { locale: it })}</h2>
            <span className="text-sm text-slate-500" data-testid="agenda-giorno-count">· {delGiorno.length} appuntament{delGiorno.length === 1 ? "o" : "i"}</span>
          </div>
          <ul className="space-y-3" data-testid="agenda-day-list">
            {delGiorno.map((a) => (
              <AppCard key={a.id} a={a} negozio={negozio} onEdit={setDlg} onStato={onStato} onDelete={onDelete} onReload={load}
                onScheda={(x) => navigate(x.area === "noleggio" ? `/noleggi?cliente=${x.cliente_id}${x.bici_noleggio_id ? `&bici=${x.bici_noleggio_id}` : ""}&inizio=${x.data}T${x.ora}` : x.non_registrato ? `/schede/nuova?nome=${encodeURIComponent(x.cliente_nome)}&tel=${encodeURIComponent(x.cliente_telefono || "")}` : `/schede/nuova?cliente=${x.cliente_id}${x.bici_id ? `&bici=${x.bici_id}` : ""}`)} />
            ))}
            {!delGiorno.length && <li className="rounded-md border border-dashed border-slate-200 bg-white py-14 text-center text-sm text-slate-500" data-testid="agenda-empty">Nessun appuntamento in questo giorno.</li>}
          </ul>
        </section>
      </div>
      <AppuntamentoDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} item={dlg} clienti={clienti} bici={bici} flotta={flotta} onSaved={onSaved} />
    </div>
  );
}
